import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  Paper,
  Typography,
} from '@mui/material';
import MigrationItemViewDTO from '@src/adapters/dto/migration/MigrationItemViewDTO';
import { useMemo } from 'react';

interface Props {
  items: MigrationItemViewDTO[];
  open: boolean;
  close(): void;
}

interface ItemTypes {
  name: string;
  order: number;
}

interface GroupedItems {
  name: string;
  order: number;
  items: MigrationItemViewDTO[];
}

const leftItemCategories: ItemTypes[] = [
  { name: 'Trays & Instrument Sets', order: 1 },
  { name: 'Supplementary Instruments', order: 2 },
];

const centerItemCategories: ItemTypes[] = [
  { name: 'Sutures', order: 1 },
  { name: 'Dressings', order: 2 },
  { name: 'Blades', order: 3 },
  { name: 'Hypodermics', order: 4 },
  { name: 'Fluids', order: 5 },
  { name: 'Infiltration', order: 6 },
  { name: 'Skin Preparation', order: 7 },
  { name: 'Table Extras', order: 8 },
];

const rightItemCategories: ItemTypes[] = [{ name: 'Consumables', order: 1 }];

const groupItems = (items: MigrationItemViewDTO[], types: ItemTypes[]) => {
  const filteredItems = items.filter((i) =>
    types.some((li) => li.name === i.category.name)
  );

  const groupedItems = filteredItems.reduce<GroupedItems[]>((acc, item) => {
    let group = acc.find((g) => g.name === item.category.name);
    if (!group) {
      const type = types.find((t) => t.name === item.category.name);
      group = { name: type!.name, order: type!.order, items: [] };
      acc.push(group);
    }

    group.items.push(item);

    return acc;
  }, []);

  return groupedItems.sort((a, b) => a.order - b.order);
};

function PreviewDialog({ items, open, close }: Props) {
  const leftItems = useMemo(() => {
    return groupItems(items, leftItemCategories);
  }, [items]);

  const centerItems = useMemo(() => {
    return groupItems(items, centerItemCategories);
  }, [items]);

  const rightItems = useMemo(() => {
    return groupItems(items, rightItemCategories);
  }, [items]);

  return (
    <Dialog
      open={open}
      aria-labelledby="Preview"
      fullScreen
      sx={{ margin: '20px' }}>
      <DialogTitle
        id="alert-dialog-title"
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(2),
        })}>
        Card Preview
      </DialogTitle>
      <DialogContent>
        <Box sx={{ flexGrow: 1, p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4.2}>
              {leftItems.map((category) => (
                <Paper elevation={3} sx={{ p: 2, m: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {category.name}
                  </Typography>
                  <List>
                    {category.items.map((item) => (
                      <ListItem key={item.id}>{item.inventory.name}</ListItem>
                    ))}
                  </List>
                </Paper>
              ))}
            </Grid>
            <Grid item xs={12} md={3.6}>
              {centerItems.map((category) => (
                <Paper elevation={3} sx={{ p: 2, m: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {category.name}
                  </Typography>
                  <List>
                    {category.items.map((item) => (
                      <ListItem key={item.id}>{item.inventory.name}</ListItem>
                    ))}
                  </List>
                </Paper>
              ))}
            </Grid>
            <Grid item xs={12} md={4.2}>
              {rightItems.map((category) => (
                <Paper elevation={3} sx={{ p: 2, m: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {category.name}
                  </Typography>
                  <List>
                    {category.items.map((item) => (
                      <ListItem key={item.id}>{item.inventory.name}</ListItem>
                    ))}
                  </List>
                </Paper>
              ))}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PreviewDialog;
